import { create } from 'zustand'
import type {
  CloudDeliveryMode,
  CloudRunMeta,
  GitHubConnectionStatus,
  GitHubRepo,
  LlmProvider,
  LlmProviderStatus,
  RunStatus
} from '../shared/types'
import { PRD_SYSTEM_PROMPT } from '../shared/prd-system'

interface AppState {
  authenticated: boolean
  setAuthenticated: (value: boolean) => void

  systemPrompt: string
  userPrompt: string
  setSystemPrompt: (value: string) => void
  setUserPrompt: (value: string) => void
  resetSystemPrompt: () => void

  maxIterations: number
  completionPromise: string
  maxBudgetUsd: number
  llmProvider: LlmProvider
  llmStatus: LlmProviderStatus | null
  llmLoading: boolean
  llmError: string
  deliveryMode: CloudDeliveryMode
  selectedRepoFullName: string
  selectedBaseBranch: string
  setMaxIterations: (value: number) => void
  setCompletionPromise: (value: string) => void
  setMaxBudgetUsd: (value: number) => void
  setLlmProvider: (value: LlmProvider) => void
  setLlmStatus: (value: LlmProviderStatus | null) => void
  setLlmLoading: (value: boolean) => void
  setLlmError: (value: string) => void
  setDeliveryMode: (value: CloudDeliveryMode) => void
  setSelectedRepoFullName: (value: string) => void
  setSelectedBaseBranch: (value: string) => void

  githubStatus: GitHubConnectionStatus | null
  githubRepos: GitHubRepo[]
  githubLoading: boolean
  githubError: string
  setGitHubStatus: (value: GitHubConnectionStatus | null) => void
  setGitHubRepos: (value: GitHubRepo[]) => void
  setGitHubLoading: (value: boolean) => void
  setGitHubError: (value: string) => void

  prdContent: string
  appendPrd: (text: string) => void
  setPrdContent: (value: string) => void
  clearPrd: () => void

  currentRun: CloudRunMeta | null
  setCurrentRun: (value: CloudRunMeta | null) => void

  status: RunStatus
  iteration: number
  maxIterationsRunning: number
  startTime: number | null
  setStatus: (status: RunStatus) => void
  setIteration: (iteration: number, max: number) => void
  setStartTime: (time: number | null) => void
}

export const useAppStore = create<AppState>((set) => ({
  authenticated: false,
  setAuthenticated: (value) => set({ authenticated: value }),

  systemPrompt: PRD_SYSTEM_PROMPT,
  userPrompt: '',
  setSystemPrompt: (value) => set({ systemPrompt: value }),
  setUserPrompt: (value) => set({ userPrompt: value }),
  resetSystemPrompt: () => set({ systemPrompt: PRD_SYSTEM_PROMPT }),

  maxIterations: 20,
  completionPromise: 'DONE',
  maxBudgetUsd: 5,
  llmProvider: 'auto',
  llmStatus: null,
  llmLoading: false,
  llmError: '',
  deliveryMode: 'draft-pr',
  selectedRepoFullName: '',
  selectedBaseBranch: '',
  setMaxIterations: (value) => set({ maxIterations: value }),
  setCompletionPromise: (value) => set({ completionPromise: value }),
  setMaxBudgetUsd: (value) => set({ maxBudgetUsd: value }),
  setLlmProvider: (value) => set({ llmProvider: value }),
  setLlmStatus: (value) => set({ llmStatus: value }),
  setLlmLoading: (value) => set({ llmLoading: value }),
  setLlmError: (value) => set({ llmError: value }),
  setDeliveryMode: (value) => set({ deliveryMode: value }),
  setSelectedRepoFullName: (value) => set({ selectedRepoFullName: value }),
  setSelectedBaseBranch: (value) => set({ selectedBaseBranch: value }),

  githubStatus: null,
  githubRepos: [],
  githubLoading: false,
  githubError: '',
  setGitHubStatus: (value) => set({ githubStatus: value }),
  setGitHubRepos: (value) => set((state) => {
    const selectedRepo =
      value.find((repo) => repo.fullName === state.selectedRepoFullName) ?? value[0]

    return {
      githubRepos: value,
      selectedRepoFullName: selectedRepo?.fullName ?? '',
      selectedBaseBranch: selectedRepo?.defaultBranch ?? state.selectedBaseBranch
    }
  }),
  setGitHubLoading: (value) => set({ githubLoading: value }),
  setGitHubError: (value) => set({ githubError: value }),

  prdContent: '',
  appendPrd: (text) => set((s) => ({ prdContent: s.prdContent + text })),
  setPrdContent: (value) => set({ prdContent: value }),
  clearPrd: () => set({ prdContent: '' }),

  currentRun: null,
  setCurrentRun: (value) => set({ currentRun: value }),

  status: 'idle',
  iteration: 0,
  maxIterationsRunning: 0,
  startTime: null,
  setStatus: (status) => set({ status }),
  setIteration: (iteration, max) => set({ iteration, maxIterationsRunning: max }),
  setStartTime: (time) => set({ startTime: time })
}))
